/**
 * Listing-create builder — the boundary between listing composers
 * (`card-review-step.tsx`, `review-step.tsx`, `ChatSellItemSheet.tsx`) and the
 * HTTP route `app/api/listings/create/route.ts`.
 *
 *   components/publications/.../card-review-step.tsx        ┐
 *   components/publications/.../review-step.tsx             ┼── intent ──▶ createListing
 *   components/chat/ChatSellItemSheet.tsx                   ┘                      │
 *                                                                                 ├── safeParse (boundary)
 *                                                                                 ├── seal → Built<CreateListingRequest>
 *                                                                                 └── postJson  ─── 2xx ──▶ CreateListingResult
 *                                                                                                 │
 *                                                                                                 └── !ok ──▶ throws CreateListingError(status, serverError)
 *
 * Why this exists:
 *  - Sibling-E batch 2 F1 (`.tondocs/RELIABILITY_ROADMAP.md §3a`): three
 *    web producers all hand-assembled a body literal and called `fetch`
 *    inline — full cycle (no schema, no boundary parse, no canonical
 *    builder). Builder consolidates the three so the integration test
 *    `tests/integration/listings-create.test.ts` (last (c)-blocked from
 *    E5) can drive the producer + consumer in one call.
 *  - 2nd Built<T> increment (§4 #3c): re-uses the chat-batch seam
 *    (`lib/api/post-json.ts` + `seal()`); a hand-rolled literal in a
 *    component cannot reach `fetch('/api/listings/create')` without
 *    going through this builder.
 *
 * Design notes:
 *  - Intent shape ≡ wire shape (`CreateListingRequest`). The producers
 *    already build snake_case shapes from form state; mapping intent →
 *    wire would just be a busywork rename. Spread-and-trust: the union's
 *    `cardCategory: "trading_card"` literal narrows the safeParse to the
 *    Card variant; absence (or `undefined`) narrows to Lego.
 *  - Response shape is a single field (`{ listingId: string }`) and is
 *    NOT pinned by Zod (same reasoning as `chat-send.ts`,
 *    `offer-builders.ts`). Tightening would require a response contract
 *    PR — out of scope.
 *  - On `!res.ok` the builder throws `CreateListingError(message, status,
 *    serverError)`. Delivery layer in each component is responsible for
 *    mapping to user-facing UI (toast/setState/cleanup uploaded images).
 *  - `clientMessage` fallback on error: legacy producers fell back to a
 *    component-specific i18n string. The builder exposes the raw server
 *    error and status; the component maps to its own i18n key.
 *
 * @see app/api/listings/create/route.ts — handler with boundary safeParse
 * @see apps/mobile/lib/listing-create-api.ts — mobile sibling builder
 *      (older "result envelope" shape: `{ ok: boolean, listingId | message }`
 *      — kept stable to avoid mobile-rebuild; web builder uses
 *      throw-on-error mirror of chat-send.ts)
 * @see packages/contract/src/listing-create.ts — CreateListingRequestSchema
 * @see lib/chat/chat-send.ts — reference template (Sibling-E batch 1)
 * @see lib/api/post-json.ts — Built<T> transport seam
 * @see .tondocs/RELIABILITY_ROADMAP.md §3a Sibling-E F1 (batch 2)
 * @see .tondocs/skills/hex.md §2 — Functional Core / Imperative Shell
 */

import {
  CreateListingRequestSchema,
  seal,
  type CreateListingRequest,
} from "@kraahl/contract"
import { postJson } from "@/lib/api/post-json"

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Thrown when the assembled body fails the contract Zod parse before fetch.
 * Programmer-bug signal: caller passed an intent the contract refuses. The
 * server would 400 anyway; this surfaces it at the responsible line.
 */
export class CreateListingContractError extends Error {
  readonly issues: ReadonlyArray<{ path: string; message: string }>
  constructor(issues: ReadonlyArray<{ path: string; message: string }>) {
    super(
      `[listings/create] contract parse failed: ${issues
        .map((i) => `${i.path}: ${i.message}`)
        .join("; ")}`,
    )
    this.name = "CreateListingContractError"
    this.issues = issues
  }
}

/**
 * Thrown when `/api/listings/create` returns a non-2xx response. The
 * delivery layer reads `status` and `serverError` (the raw `error` string
 * from the payload) to produce its user-facing message. The previous inline
 * call-sites all read `payload.error` from a `!res.ok` branch — same shape.
 */
export class CreateListingError extends Error {
  readonly status: number
  readonly serverError: string | null
  constructor(message: string, status: number, serverError: string | null) {
    super(message)
    this.name = "CreateListingError"
    this.status = status
    this.serverError = serverError
  }
}

// ---------------------------------------------------------------------------
// Intent + result types
// ---------------------------------------------------------------------------

/**
 * Intent ≡ wire shape (the discriminated `CreateListingRequest` union from
 * `@kraahl/contract`). Mapping to a friendlier intent would just rename
 * snake-case keys producers already use; not worth the per-producer churn.
 */
export type CreateListingIntent = CreateListingRequest

export interface CreateListingResult {
  /** Server-resolved listing id. Routes pin this from the inserted row. */
  listingId: string
}

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

/**
 * Build, safeParse, POST `/api/listings/create`, and return the resolved
 * `{ listingId }` on success. Throws `CreateListingError(message, status,
 * serverError)` on any non-2xx — the delivery layer in the component is
 * expected to map it to user-facing UI (toast / cleanup orphaned image
 * uploads / setComposerError on a 422 moderation reject).
 */
export async function createListing(
  intent: CreateListingIntent,
): Promise<CreateListingResult> {
  // Spread-and-trust: the discriminator `cardCategory: "trading_card"`
  // narrows the safeParse to the Card variant; absence narrows to Lego.
  // The contract's union accepts both shapes byte-truthfully against the
  // three producers (verified in PR description, field-loss diff).
  const body = { ...intent } satisfies CreateListingRequest

  const parsed = CreateListingRequestSchema.safeParse(body)
  if (!parsed.success) {
    throw new CreateListingContractError(
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    )
  }

  // Sibling-E §4 #3c: `seal` after safeParse converts the body into a
  // `Built<CreateListingRequest>`. `postJson` accepts ONLY `Built<T>` — a
  // hand-rolled literal in a component cannot reach this seam.
  const res = await postJson(
    "/api/listings/create",
    seal<CreateListingRequest>(body),
  )

  if (!res.ok) {
    const errPayload = await res.json().catch(() => null)
    const serverError =
      errPayload && typeof (errPayload as { error?: unknown }).error === "string"
        ? ((errPayload as { error: string }).error)
        : null
    const message = serverError ?? `Create listing (${res.status})`
    throw new CreateListingError(message, res.status, serverError)
  }

  const json = (await res.json()) as { listingId?: unknown }
  if (typeof json.listingId !== "string" || json.listingId.length === 0) {
    throw new CreateListingError(
      "Create listing: missing listingId in response",
      res.status,
      null,
    )
  }
  return { listingId: json.listingId }
}
